// Single source of truth for Domain Intelligence severity handling.
//
// Canonical levels and aliases mirror the engine's SEVERITY_RANKS
// (scoop: SCHEMA.md §E "Severity Ranking (v2.9.34)", HierarchyRollupSynthesizer):
//
//   rank 0  Severe    aliases: critical
//   rank 1  Moderate  aliases: concerning
//   rank 2  Mild      aliases: watch
//   rank 3  Healthy   aliases: stable
//
// Every component MUST get severity rank, color, and label from here. The
// 2026-07 audit (F3) found three disagreeing local tables; real run values
// like "Watch" rendered as unstyled grey and mis-sorted.

const LEVELS = {
    severe: { rank: 0, label: 'Severe', color: 'error', flagged: true },
    moderate: { rank: 1, label: 'Moderate', color: 'warning', flagged: true },
    mild: { rank: 2, label: 'Mild', color: 'info', flagged: true },
    healthy: { rank: 3, label: 'Healthy', color: 'success', flagged: false },
}

const ALIASES = {
    critical: 'severe',
    concerning: 'moderate',
    watch: 'mild',
    stable: 'healthy',
    // Legacy UI-only spellings observed in older ad-hoc runs; mapped so they
    // never fall through to unknown styling.
    high: 'severe',
    medium: 'moderate',
    low: 'mild',
}

export const UNKNOWN_RANK = 4

/** Canonical lowercase key for a severity value, or 'unknown'. */
export const normalizeSeverity = (severity) => {
    const raw = String(severity ?? '')
        .trim()
        .toLowerCase()
    if (!raw) return 'unknown'
    if (LEVELS[raw]) return raw
    if (ALIASES[raw]) return ALIASES[raw]
    return 'unknown'
}

/** 0 (Severe, worst) … 3 (Healthy, best); unknown sorts after Healthy, matching the engine. */
export const severityRank = (severity) => {
    const key = normalizeSeverity(severity)
    return LEVELS[key]?.rank ?? UNKNOWN_RANK
}

/** Chip/badge color token for any severity value. Unknown gets 'default', never a canonical value. */
export const severityColor = (severity) => {
    const key = normalizeSeverity(severity)
    return LEVELS[key]?.color ?? 'default'
}

/** Canonical display label (e.g. 'watch' -> 'Mild'). Unknown values echo trimmed input or 'Unknown'. */
export const severityLabel = (severity) => {
    const key = normalizeSeverity(severity)
    if (LEVELS[key]) return LEVELS[key].label
    const raw = String(severity ?? '').trim()
    return raw || 'Unknown'
}

/** Whether this severity alone marks the entity as flagged. Unknown is NOT flagged (matches backend isFlaggedEntity). */
export const isFlaggedSeverity = (severity) => {
    const key = normalizeSeverity(severity)
    return LEVELS[key]?.flagged ?? false
}

/** True when the value maps to a canonical level (i.e. the engine could have produced it). */
export const isKnownSeverity = (severity) => normalizeSeverity(severity) !== 'unknown'

export const CANONICAL_SEVERITIES = Object.keys(LEVELS)
export const SEVERITY_ALIASES = { ...ALIASES }
